import React, { useMemo } from 'react'
import { IJsonResponse } from 'core/JsonApiSerializer'
import { utilService } from 'core/services'
import moment from 'moment'
import { IFeature } from 'static/interfaces'
import { ISelectedGuests } from 'utils/reservation'
import styled from 'styled-components'
import Classnames from 'classnames'
import { MobileHeaderSearchPanel } from 'components/atoms/MobileSearchPanel'
import { COLORS, DEFAULT_SEARCH_FILTER_GUESTS } from 'static/constants'
import { getSearchFiltersCookie, updateSearchFiltersCookie } from 'utils/searchFiltersCookie'
import { resolveInitialSearchAreaFeature } from 'utils/searchAreaFeature'
import SvgIcon from 'components/atoms/SvgIcon'

interface IProps {
  searchBoxIsOpen: boolean
  areaFeatures: IJsonResponse
  setSearchBoxIsOpen: (isOpen: boolean) => void
  currentFeature?: { slug: string; feature_type: string } | null
}

interface IMobileSearchProps {
  date: moment.Moment | null
  guests: ISelectedGuests
  areaFeature: IFeature | null
  setSearchBoxIsOpen: (isOpen: boolean) => void
}

const MobileSearch: React.VFC<IMobileSearchProps> = ({
  date,
  guests,
  areaFeature,
  setSearchBoxIsOpen,
}) => {
  const mergedFilterValueLabel = useMemo(() => {
    const dateLabel = date ? `${date.format('MMM DD')}` : null
    // 合計人数を計算
    const totalGuests = (guests?.adults || 0) + (guests?.children || 0) + (guests?.infants || 0)
    // ゲスト数のラベルを作成
    const guestsLabel = totalGuests > 0 ? `${totalGuests} Guest${totalGuests > 1 ? 's' : ''}` : null
    const areaLabel = areaFeature ? areaFeature.title : null
    const filterLabels = [areaLabel, dateLabel, guestsLabel].filter(Boolean)

    return filterLabels.length > 0 ? filterLabels.join(', ') : null
  }, [date, guests, areaFeature])

  return (
    <SearchBoxMobile onClick={() => setSearchBoxIsOpen(true)}>
      {!mergedFilterValueLabel && (
        <SvgIcon name="search" className="icon" ariaHidden={true} role="presentation" />
      )}
      <div
        className={Classnames('MobileSearch_Filter_Title', {
          hasFilter: mergedFilterValueLabel !== null,
        })}
      >
        {mergedFilterValueLabel ?? 'Search Tours'}
      </div>
    </SearchBoxMobile>
  )
}

// TODO 親コンポーネントつくってフィルタの状態を親が持って子に渡すように作り変える
export const MobileSearchParent: React.VFC<IProps> = ({
  areaFeatures,
  searchBoxIsOpen,
  setSearchBoxIsOpen,
  currentFeature,
}) => {
  const { data: parsedAreaFeatures } = utilService.getDataFromJson(areaFeatures)
  const searchFiltersCookie = getSearchFiltersCookie()

  const initialFeature = resolveInitialSearchAreaFeature({
    currentFeatureSlug: currentFeature?.slug ?? null,
    currentFeatureType: currentFeature?.feature_type ?? null,
    pathname: window.location.pathname,
    parsedAreaFeatures,
    searchFiltersAreaFeature: searchFiltersCookie?.areaFeature ?? null,
  })
  const initialDate = searchFiltersCookie?.date ?? null
  const initialGuests = searchFiltersCookie?.guests ?? DEFAULT_SEARCH_FILTER_GUESTS

  React.useEffect(() => {
    const cookieAreaSlug = searchFiltersCookie?.areaFeature?.slug ?? null
    const resolvedAreaFeature = initialFeature
      ? { id: initialFeature.id, slug: initialFeature.slug, title: initialFeature.title }
      : null
    const resolvedAreaSlug = resolvedAreaFeature?.slug ?? null

    if (cookieAreaSlug === resolvedAreaSlug) {
      return
    }

    updateSearchFiltersCookie({
      areaFeature: resolvedAreaFeature,
    })
  }, [initialFeature, searchFiltersCookie?.areaFeature?.slug])

  const [areaFeature, setAreaFeature] = React.useState<IFeature | null>(initialFeature ?? null)
  const [date, setDate] = React.useState<moment.Moment | null>(
    initialDate ? moment(initialDate) : null
  )
  const [guests, setGuests] = React.useState<ISelectedGuests>({
    adults: initialGuests.adults,
    children: initialGuests.children,
    infants: initialGuests.infants,
  })
  return (
    <>
      <MobileSearch
        date={date}
        guests={guests}
        areaFeature={areaFeature}
        setSearchBoxIsOpen={setSearchBoxIsOpen}
      />
      {searchBoxIsOpen && (
        <MobileHeaderSearchPanel
          setSearchBoxIsOpen={setSearchBoxIsOpen}
          areaFeature={areaFeature}
          setAreaFeature={setAreaFeature}
          date={date}
          setDate={setDate}
          guests={guests}
          setGuests={setGuests}
          areaFeatures={parsedAreaFeatures}
        />
      )}
    </>
  )
}

export const SearchBoxMobile = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 13px 20px;
  gap: 12px;

  margin: 0 23px;
  width: 216px;
  height: 48px;

  background: #ffffff;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.15);
  border-radius: 40px;
  .MobileSearch_Filter_Title {
    font-size: 16px;
    &.hasFilter {
      font-size: 13px;
      color: ${COLORS.subText};
    }
  }
`
